import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import React from 'react';
// Fase 5.10: es un NodeView de Tiptap (`ReactNodeViewRenderer`), lo instancia el editor dentro
// de su propio árbol de React — hereda el contexto del Router pero no hay un prop path desde un
// container. Excepción documentada.
// eslint-disable-next-line no-restricted-imports
import { useNavigate } from 'react-router-dom';

// Integración main→dev, epic D2: el click en un doc-tag interno emitía un `window` CustomEvent
// (`doc-navigate`) — prohibido por política. Se reemplaza por `useNavigate` de React Router
// (el NodeView hereda el contexto del Router porque `ReactNodeViewRenderer` renderiza dentro
// del árbol de React del editor).
const DocTagView = ({ node, editor }) => {
    const navigate = useNavigate();
    const { url, emoji, text, external, color } = node.attrs;
    const isExternal = external === 'true' || external === true;
    // Integración main→dev (catch-up F, `323983ff`): en modo edición un click en el tag no
    // navega (solo previene el default); sin `target="_blank"` para que ese click nunca abra
    // una pestaña por accidente al estar editando.
    const isEditable = editor?.isEditable;

    let styleAttr = {};
    if (color) {
        styleAttr = { color, background: `${color}26`, border: `1px solid ${color}33` };
    }

    const handleNavigation = () => {
        if (!url) return;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
        <NodeViewWrapper as="span" style={{ display: 'inline-block' }}>
            <a
                href={url}
                className={`doc-tag ${isExternal ? 'external' : ''}`}
                contentEditable="false" suppressContentEditableWarning
                style={styleAttr}
                onClick={(e) => {
                    e.preventDefault();
                    // En modo edición, 1 clic solo selecciona (abre LilMenu). 
                    if (isEditable) return; 
                    // En modo lectura, 1 clic navega normal.
                    handleNavigation();
                }}
                onDoubleClick={(e) => {
                    e.preventDefault();
                    // En modo edición, 2 clics navega al link.
                    if (isEditable) {
                        handleNavigation();
                    }
                }}
            >
                {emoji && <span className="doc-tag-emoji">{emoji}</span>}
                <span className="doc-tag-text">{text}</span>
            </a>
        </NodeViewWrapper>
    );
};

export const DocTagExtension = Node.create({
    name: 'docTag',
    priority: 1000,
    group: 'inline',
    inline: true,
    atom: true,

    // Integración main→dev, epic D2: `href` real + `data-*` para round-trip en save+reload
    // (`main` serializaba `url`/`emoji`/`text` como atributos sueltos que `parseHTML` no leía).
    addAttributes() {
        return {
            url: {
                default: '#',
                parseHTML: (e) => e.getAttribute('href') || '#',
                renderHTML: (a) => ({ href: a.url || '#' }),
            },
            emoji: {
                default: '📄',
                parseHTML: (e) => e.getAttribute('data-emoji') || e.querySelector('.doc-tag-emoji')?.textContent || '📄',
                renderHTML: (a) => (a.emoji ? { 'data-emoji': a.emoji } : {}),
            },
            text: {
                default: '',
                parseHTML: (e) => e.getAttribute('data-text') || e.querySelector('.doc-tag-text')?.textContent || '',
                renderHTML: (a) => (a.text ? { 'data-text': a.text } : {}),
            },
            external: {
                default: false,
                parseHTML: (e) => e.classList.contains('external'),
                renderHTML: (a) => (a.external ? { class: 'doc-tag external' } : { class: 'doc-tag' }),
            },
            color: {
                default: null,
                parseHTML: (e) => e.getAttribute('data-color') || e.style.color || null,
                renderHTML: (a) => (a.color ? { 'data-color': a.color, style: `color:${a.color}` } : {}),
            },
        };
    },

    parseHTML() {
        return [
            { tag: 'span[data-type="docTag"]' },
            { tag: 'a.doc-tag' } // Para retrocompatibilidad
        ];
    },

    renderHTML({ HTMLAttributes }) {
        // Serializamos como span para que el Mark "Link" de Tiptap no lo secuestre y destruya
        return ['span', mergeAttributes({ class: 'doc-tag', 'data-type': 'docTag' }, HTMLAttributes)];
    },

    renderText({ node }) {
        return `${node.attrs.text || ''} (${node.attrs.url || ''})`.trim();
    },

    addNodeView() {
        return ReactNodeViewRenderer(DocTagView);
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey('docTagPasteHandler'),
                props: {
                    handlePaste: (view, event) => {
                        const text = event.clipboardData?.getData('text/plain');
                        if (!text) return false;

                        // Check if text is just a single valid URL
                        const urlRegex = /^(https?:\/\/[^\s]+)$/i;
                        if (!urlRegex.test(text.trim())) return false;

                        const urlStr = text.trim();
                        try {
                            const url = new URL(urlStr);
                            const isInternal = url.origin === window.location.origin;

                            let tagText = url.hostname.replace(/^www\./, ''); // e.g. google.com
                            let external = true;

                            if (isInternal) {
                                external = false;
                                const path = url.pathname;
                                
                                if (path.includes('/archivos') || url.hostname.includes('storage.googleapis.com')) {
                                    tagText = 'archivo: ' + path.split('/').pop();
                                } else if (path.includes('/task/list/')) {
                                    const parts = path.split('/').filter(Boolean);
                                    tagText = 'tarea: ' + (parts.pop() || 'Cargando...'); 
                                } else if (path.includes('/docs/')) {
                                    const parts = path.split('/').filter(Boolean);
                                    tagText = 'doc: ' + (parts.pop() || 'Cargando...');
                                } else if (path.includes('/calendar') || path.includes('/event/')) {
                                    const parts = path.split('/').filter(Boolean);
                                    tagText = 'evento: ' + (parts.pop() || 'Cargando...');
                                } else if (path.includes('/s/') && path.split('/').length >= 4) {
                                    tagText = 'session: pública';
                                } else {
                                    const spaceMatch = path.match(/^\/([^/]+)/);
                                    if (spaceMatch && !['all', 'p', 's', 'pay', 'landing', 'login'].includes(spaceMatch[1])) {
                                        tagText = 'espacio: ' + spaceMatch[1];
                                    } else {
                                        tagText = 'link interno';
                                    }
                                }
                            }

                            // Insert the docTag node
                            const { schema } = view.state;
                            const node = schema.nodes.docTag.create({
                                url: urlStr,
                                emoji: '',
                                text: tagText,
                                external
                            });

                            const tr = view.state.tr.replaceSelectionWith(node, false);
                            view.dispatch(tr);
                            return true; // We handled the paste
                        } catch (e) {
                            return false;
                        }
                    }
                }
            })
        ];
    }
});
